import { getTimezoneByUtc } from 'app/pages/account/userAccount/schedule/utils';
import { ICombinedUserData } from 'lib/context';

const churnUserTypeMap: Record<number, string> = {
  1: 'User',
  3: 'Company Admin',
  4: 'Supervisor',
};
const churnAutoRoleMap: Record<number, string> = {
  1: 'Technician',
  2: 'Service Advisor',
  3: 'Service Manger',
  4: 'Salesperson',
  5: 'Sales Manager',
  6: 'BDC',
};

const toYesNoString = (value: boolean): string => {
  return value ? 'Yes' : 'No';
};

const defaultIfBlank = (value: string | null | undefined): string => {
  return value?.trim() ? value : '-';
};

const toISOStringOrUndefined = (date?: string | number): string | undefined => {
  if (!date) return undefined;
  return new Date(date).toISOString();
};

const getAccountAttributes = (
  userData: ICombinedUserData
): Record<string, string | boolean | number | undefined> => {
  const { customer } = userData;
  return {
    Name: defaultIfBlank(customer.business),
    ResellerId: customer.resellerId,
    CustomerId: customer.customerId,
    ResellerName: defaultIfBlank(userData?.reseller?.resellerName),
    ResellerUrl: defaultIfBlank(userData?.reseller?.url),
    ResellerPlaybackUrl: defaultIfBlank(userData?.reseller?.playbackUrl),
    AccountUrl: defaultIfBlank(customer?.url),
    AudiDealerCode: defaultIfBlank(customer?.audiDealerCode),
    AudiAreaCode: defaultIfBlank(customer?.audiAreaCode),
    AudiSubAreaCode: defaultIfBlank(customer?.audiSubAreaCode),
    FullReportAccess: toYesNoString(!!userData?.reseller?.fullReportAccess),
    Dealer: toYesNoString(!!userData?.reseller?.dealer),
    ResellerReportingEnabled: toYesNoString(
      !!userData?.reseller?.resellerReportingEnabled
    ),
    FolderSharingAdminOnly: toYesNoString(
      !!userData?.reseller?.restrictFolderSharingToAdmins
    ),
    ResellerCreatedAt: toISOStringOrUndefined(userData?.reseller?.createdAt),
    HasMlm: toYesNoString(!!customer?.hasMultiLocation),
    ParentCustomerId: customer?.parentCustomerId,
    LocationId: customer?.locationId,
    FreeTrial: toYesNoString(!!customer?.freeTrial),
    SmsEnabled: toYesNoString(!!customer?.smsEnabled),
    AutomotiveAccess: toYesNoString(!!customer?.automotiveAccess),
    LeadsIntegrationActive: toYesNoString(!!customer?.leadsIntegrationActive),
    AiAssistActivated: toYesNoString(!!customer?.hasCovideoMagic),
    DeactivationDate: toISOStringOrUndefined(customer?.deactivationDate),
    LiveMeetingAccess: toYesNoString(!!customer?.liveAccess),
    AccountCreated: toISOStringOrUndefined(customer?.createdAt),
    TimeZone: getTimezoneByUtc(customer.timezone)?.label,
    WeeklyReportEnabled: Boolean(
      parseInt(customer?.weeklyReportEnabled?.toString())
    ),
    ServiceLeaderboardReportEnabled: toYesNoString(
      !!customer?.leaderboardReportEnabled
    ),
    AudioCancelEnabled: toYesNoString(!!customer?.hasAudioDenoise),
  };
};

const getContactAttributes = (
  userData: ICombinedUserData
): Record<string, string | boolean | number | undefined> => {
  return {
    FirstName: defaultIfBlank(userData.firstName),
    LastName: defaultIfBlank(userData.lastName),
    UserId: defaultIfBlank(userData.id?.toString()),
    CustomerId: defaultIfBlank(userData.customerId?.toString()),
    ResellerId: defaultIfBlank(userData.resellerId?.toString()),
    ParentUserId: defaultIfBlank(userData.parentUserId?.toString()),
    Username: defaultIfBlank(userData?.username),
    Department: defaultIfBlank(userData.dept),
    Title: defaultIfBlank(userData.title),
    UserType: defaultIfBlank(churnUserTypeMap[parseInt(userData.access)]),
    UserAddedBy: defaultIfBlank(userData.addedBy),
    AutoRole: defaultIfBlank(
      churnAutoRoleMap[parseInt(userData.automotiveRole)]
    ),
    ParentCustomerId: userData.parentCustomerId?.toString(),
    Email: userData.email?.trim(),
    Email2: defaultIfBlank(userData.email2?.trim()),
    Phone1: userData.phone1,
    Phone2: defaultIfBlank(userData.phone2),
    UserAccountVerified: toYesNoString(!!parseInt(userData.verified)),
    TrialAccount: toYesNoString(!!userData.trialAccount),
    LastLoginDate: userData.loginDate
      ? new Date(Number(userData.loginDate) * 1000).toISOString()
      : undefined,
    ResellerAdmin: toYesNoString(!!parseInt(userData.resellerAdmin)),
    UserDeactivationDate: userData.deactivationDate
      ? new Date(userData.deactivationDate).toISOString()
      : undefined,
    ManualPwUsed: toYesNoString(!!userData.manualPassword),
    CellPhone: defaultIfBlank(userData.cellPhone),
  };
};

export const initChurnZero = (userData: ICombinedUserData) => {
  if (!window.ChurnZero) return;

  window.ChurnZero.push(['silent', true]);
  window.ChurnZero.push(['setContact', userData.customerId, userData.id]);
  window.ChurnZero.push([
    'setAttribute',
    'account',
    getAccountAttributes(userData),
  ]);
  window.ChurnZero.push([
    'setAttribute',
    'contact',
    getContactAttributes(userData),
  ]);
};
